/**
 * @file ProductRegulatorySection.tsx
 * @description «Регуляторные поля» (phase08.5 shadcn).
 */

import { ShieldCheck } from "lucide-react";

import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Switch } from "../../../../components/ui/switch";
import type { CatalogCategory } from "../../../../lib/api-catalog";
import type { ProductFormState } from "../product-form";
import { SectionCard } from "./SectionCard";

interface Props {
  form: ProductFormState;
  category: CatalogCategory | null;
  onChange: (patch: Partial<ProductFormState>) => void;
  disabled?: boolean;
}

export function ProductRegulatorySection({
  form,
  category,
  onChange,
  disabled,
}: Props) {
  const isHerbsOrBerries =
    category?.slug === "herbs" || category?.slug === "berries";
  return (
    <SectionCard icon={<ShieldCheck />} title="Регуляторные поля" collapsible>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="r-allerg">Аллергены (через запятую)</Label>
          <Input
            id="r-allerg"
            value={form.allergensCsv}
            onChange={(e) => onChange({ allergensCsv: e.target.value })}
            disabled={disabled}
          />
          <p className="type-caption text-on-surface-variant">
            Для пищевых: глютен, лактоза, орехи. ТР ТС 022/2011 — 14 аллергенов
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch
              checked={form.badWarning}
              onCheckedChange={(c) => onChange({ badWarning: c === true })}
              disabled={disabled}
            />
            <span className="text-sm text-on-surface">
              Плашка «Не является лекарственным средством»
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch
              checked={form.notForConsumption}
              onCheckedChange={(c) => onChange({ notForConsumption: c === true })}
              disabled={disabled}
            />
            <span className="text-sm text-on-surface">
              Не для употребления (декоративный)
            </span>
          </label>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <div className="md:w-60 flex flex-col gap-1.5">
            <Label htmlFor="r-age">Возрастное ограничение</Label>
            <Input
              id="r-age"
              type="number"
              min={0}
              max={99}
              value={form.ageRestriction}
              onChange={(e) => onChange({ ageRestriction: e.target.value })}
              disabled={disabled}
            />
            <p className="type-caption text-on-surface-variant">0 = без ограничения</p>
          </div>
          <div className="flex-1 flex flex-col gap-1.5">
            <Label htmlFor="r-decl">
              Декларация ТР ТС 021/2011
              {isHerbsOrBerries ? " *" : ""}
            </Label>
            <Input
              id="r-decl"
              required={isHerbsOrBerries}
              value={form.declarationNumber}
              onChange={(e) => onChange({ declarationNumber: e.target.value })}
              disabled={disabled}
            />
            <p className="type-caption text-on-surface-variant">
              {isHerbsOrBerries
                ? "Для дикоросов (травы/ягоды) — обязательно для публикации"
                : "Если применимо"}
            </p>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
